import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import {emitChangedWait, getObsScore, obsAges, obsStatuses, obsTypes} from '../obsUtils';
import ObsNumericStepper from './obsNumericStepper';
import renderAcceptSection from './renderAcceptSection';
import AgyleBlockSwitch from '../../../../../components/ui/switch/agyleBlockSwitch';
import {useTheme} from '@mui/material/styles';
import {obsStyles} from '../obsStyle';

function ObsPulseRate(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const theme = useTheme();

    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [pulseRate, setPulseRate] = React.useState(allowDefaultValue ? props.pulseRate : null);
    const [pulseType, setPulseType] = React.useState(allowDefaultValue ? props.pulseType : null);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    function setDefaults() {
        if (type === obsTypes.news) {
            setPulseRate(70);
        } else if (type === obsTypes.pews) {
            if (ageRangeYears === obsAges._0_1) {
                setPulseRate(130);
            } else if (ageRangeYears === obsAges._1_5) {
                setPulseRate(105);
            } else if (ageRangeYears === obsAges._5_12) {
                setPulseRate(95);
            } else if (ageRangeYears === obsAges._12_18) {
                setPulseRate(85);
            }
        } else if (type === obsTypes.neuro) {
            if (ageRangeYears === obsAges._1_5) {
                setPulseRate(105);
            } else if (ageRangeYears === obsAges._5_16) {
                setPulseRate(95);
            } else {
                setPulseRate(70);
            }
        }
    }

    // For Mobile view
    useEffect(() => {
        if (props.ipodView && props.status !== obsStatuses.new) {
            setPulseRate(props.pulseRate);
            setPulseType(props.pulseType);
        }
    }, []);

    useEffect(() => {
        if ((_.isUndefined(pulseRate) || _.isNaN(pulseRate)) && allowDefaultValue) {
            if (_.isUndefined(props.pulseRate) || props.pulseRate == null || _.isNaN(props.pulseRate)) {
                setDefaults();
            } else {
                setPulseRate(props.pulseRate);
            }
        }
    }, [type, ageRangeYears, props.pulseRate]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        if (!allowDefaultValue) {
            setPulseRate(null);
            setPulseType(null);
        } else {
            setDefaults();
        }

        clearAccepted();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const pulseTypeCheck = type === obsTypes.news ? !_.isNull(pulseType) : true;

        const checkValue = (!_.isNull(pulseRate) && pulseTypeCheck && status === obsStatuses.new)
            || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) {
            acceptObs();
        }
    }, [pulseRate, pulseType, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    pulseRate,
                    pulseType,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [status, pulseRate, pulseType, score, accepted, acceptDate]);

    const updateScore = () => {
        let newScore = 0;
        if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
            newScore = 0;
        } else if (status === obsStatuses.patientCondition) {
            newScore = 3;
        } else {
            newScore = getObsScore('heartRate', type, ageRangeYears, pulseRate);
        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, pulseRate]);

    const onPulseRateChange = (value) => {
        setPulseRate(value);
        clearAccepted();
    };

    const onPulseTypeChange = (value) => {
        setPulseType(value);
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    {type === obsTypes.news &&
                        <Grid item xs={12}>
                            <AgyleBlockSwitch
                                onChange={onPulseTypeChange}
                                value={pulseType}
                                customLabels={{
                                    yes: t('Regular'),
                                    no: t('Irregular'),
                                }}
                                valueObject={{
                                    true: 'regular',
                                    false: 'irregular',
                                }}
                                style={{
                                    backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                                }}
                            />
                        </Grid>
                    }

                    <Grid item xs={12}>
                        <ObsNumericStepper
                            value={pulseRate}
                            minValue={0}
                            maxValue={250}
                            label={t('BPM')}
                            onChange={onPulseRateChange}
                        />
                    </Grid>

                    {renderAcceptSection({
                        classes,
                        t,
                        hideScore: type === obsTypes.neuro,
                        score,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled:
                            _.isNull(pulseRate) || _.isNull(pulseType),
                        showStatusNR: type === obsTypes.pews,
                        hideStatuses: type === obsTypes.neuro,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsPulseRate.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    pulseRate: PropTypes.number,
    pulseType: PropTypes.oneOf(['regular', 'irregular']),
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};
ObsPulseRate.defaultProps = {
    type: obsTypes.news,
    status: obsStatuses.new,
    score: 0,
    pulseType: 'regular',
    accepted: false,
    acceptDate: null,
};

export default React.memo(ObsPulseRate);